import dayjs, { Dayjs } from 'dayjs';
import { timeRanges } from './dashboard.service.types';

const elasticsearchEnabled = import.meta.env.VITE_ELASTICSEARCH_METRICS_ENABLED === 'true';

/** `1y` is opt-in per environment. */
export const visibleTimeRanges = elasticsearchEnabled
  ? timeRanges
  : timeRanges.filter((range) => range !== '1y');

const PRESET_DAYS: Record<string, number> = {
  '24h': 1,
  '3d': 3,
  '7d': 7,
  '30d': 30,
  '1y': 365,
};

/**
 * Cap on the day walk below. A hand-picked pair comes off the url, so the span is
 * caller-controlled and has no upper bound of its own.
 */
const MAX_RANGE_DAYS = 366;

type RangeSelection = {
  time_range?: string;
  start_time?: string;
  end_time?: string;
};

/**
 * A custom span reads back as a preset only when it matches exactly, else `'custom'`.
 * Picking a preset sets `time_range` and clears the pair, so this branch only ever sees a
 * hand-picked range. A day of slack made a 2-day range light up `24h`.
 */
export function inferActivePreset(value: RangeSelection): string | 'custom' | null {
  if (value.time_range) return value.time_range;

  if (value.start_time && value.end_time) {
    const days = dayjs(value.end_time).diff(dayjs(value.start_time), 'day');
    for (const [preset, presetDays] of Object.entries(PRESET_DAYS)) {
      if (days === presetDays) return preset;
    }
    return 'custom';
  }

  return null;
}

/**
 * The local calendar days a selection covers, oldest first, or none when it names no
 * window. A preset counts back from today and includes the day it lands in, so a 24-hour
 * window spans the two days it actually touches. Only the most recent MAX_RANGE_DAYS are
 * walked: a longer span is read for the shape of the office week, which repeats.
 */
export function selectedRangeDays(value: RangeSelection): Dayjs[] {
  let first: Dayjs;
  let last: Dayjs;

  if (value.start_time && value.end_time) {
    first = dayjs(value.start_time).startOf('day');
    last = dayjs(value.end_time).startOf('day');
    if (!first.isValid() || !last.isValid() || last.isBefore(first)) return [];
  } else {
    const days = value.time_range ? PRESET_DAYS[value.time_range] : undefined;
    if (days === undefined) return [];
    last = dayjs().startOf('day');
    first = last.subtract(days, 'day');
  }

  const span = last.diff(first, 'day') + 1;
  const walked = Math.min(span, MAX_RANGE_DAYS);

  return Array.from({ length: walked }, (_, index) => last.subtract(walked - 1 - index, 'day'));
}
